import CloseIcon from '@mui/icons-material/Close';
import Drawer from '@mui/material/Drawer';
import React from 'react';
import { AdminIconButton } from './AdminButton';

export type EntityDrawerCloseReason = 'closeButton' | 'escapeKeyDown' | 'backdropClick';

export interface EntityDrawerProps {
  open: boolean;
  title: React.ReactNode;
  ariaLabel?: string;
  eyebrow?: React.ReactNode;
  actions?: React.ReactNode;
  footer?: React.ReactNode;
  children: React.ReactNode;
  dirty?: boolean;
  onDirtyClose?: (reason: EntityDrawerCloseReason) => boolean | Promise<boolean>;
  onClose: (reason: EntityDrawerCloseReason) => void;
}

const EntityDrawerBackdropProps = {
  className: 'pipeex-admin-portal admin-entity-drawer-backdrop',
  'data-testid': 'entity-drawer-backdrop',
};

export const EntityDrawer: React.FC<EntityDrawerProps> = ({
  open,
  title,
  ariaLabel,
  eyebrow,
  actions,
  footer,
  children,
  dirty = false,
  onDirtyClose,
  onClose,
}) => {
  const titleId = React.useId();
  const accessibleTitle = ariaLabel ?? (typeof title === 'string' ? title : 'Entity details');

  const requestClose = React.useCallback(async (reason: EntityDrawerCloseReason) => {
    if (dirty && onDirtyClose) {
      if (!await onDirtyClose(reason)) {
        return;
      }
    }
    onClose(reason);
  }, [dirty, onClose, onDirtyClose]);

  React.useEffect(() => {
    if (!open) {
      return undefined;
    }
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        void requestClose('escapeKeyDown');
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [open, requestClose]);

  return (
    <Drawer
      anchor="right"
      open={open}
      className="pipeex-admin-portal admin-entity-drawer-root"
      transitionDuration={{ enter: 220, exit: 220 }}
      onClose={(_, reason) => void requestClose(reason)}
      ModalProps={{
        disableEscapeKeyDown: true,
        BackdropProps: EntityDrawerBackdropProps,
      }}
      PaperProps={{
        className: 'pipeex-admin-portal admin-entity-drawer',
        role: 'dialog',
        'aria-modal': true,
        'aria-labelledby': titleId,
      }}
    >
      <header className="admin-entity-drawer__header">
        <div className="admin-entity-drawer__heading">
          {eyebrow && <div className="admin-entity-drawer__eyebrow">{eyebrow}</div>}
          <div id={titleId} className="admin-entity-drawer__title">{title}</div>
        </div>
        <AdminIconButton aria-label={`Close ${accessibleTitle}`} onClick={() => void requestClose('closeButton')}>
          <CloseIcon aria-hidden="true" />
        </AdminIconButton>
      </header>
      {actions && <div className="admin-entity-drawer__actions">{actions}</div>}
      <div className="admin-entity-drawer__body">{children}</div>
      {footer && <footer className="admin-entity-drawer__footer">{footer}</footer>}
    </Drawer>
  );
};
